/**
 * Staff role ↔ Organization binding classification (browser-safe).
 *
 * Platform staff (unify-admin) belong to the singleton Unify
 * org. Practice-scoped staff belong to a type=prov Organization. Mixed sets are
 * invalid. Medplum lookups live in medplum-server.
 */

import type { Organization } from '@medplum/fhirtypes';
import {
  ORG_TYPE_OTHER,
  ORG_TYPE_PROV,
  ORG_TYPE_SYSTEM,
  PLATFORM_ORGANIZATION_IDENTIFIER_SYSTEM,
  PLATFORM_ORGANIZATION_IDENTIFIER_VALUE,
} from './constants';

export const UNIFY_PLATFORM_STAFF_ROLES = ['unify-admin'] as const;
export const PRACTICE_SCOPED_STAFF_ROLES = [
  'practice-owner',
  'practice-admin',
  'supervisor',
  'provider',
] as const;

const PLATFORM_ROLE_SET = new Set<string>(UNIFY_PLATFORM_STAFF_ROLES);
const PRACTICE_ROLE_SET = new Set<string>(PRACTICE_SCOPED_STAFF_ROLES);

export type StaffRoleOrgKind = 'platform' | 'practice' | 'mixed' | 'none';

export function isUnifyPlatformStaffRole(role: string): boolean {
  return PLATFORM_ROLE_SET.has(role);
}

export function organizationHasTypeCoding(
  org: Organization,
  system: string,
  code: string,
): boolean {
  return Boolean(
    org.type?.some((t) => t.coding?.some((c) => c.system === system && c.code === code)),
  );
}

export function organizationHasPlatformIdentifier(org: Organization): boolean {
  return Boolean(
    org.identifier?.some(
      (i) =>
        i.system === PLATFORM_ORGANIZATION_IDENTIFIER_SYSTEM
        && i.value === PLATFORM_ORGANIZATION_IDENTIFIER_VALUE,
    ),
  );
}

/** True only when identifier and type coding both match the singleton Unify org. */
export function isUnifyPlatformOrganization(org: Organization): boolean {
  return (
    organizationHasPlatformIdentifier(org)
    && organizationHasTypeCoding(org, ORG_TYPE_SYSTEM, ORG_TYPE_OTHER)
    && !organizationHasTypeCoding(org, ORG_TYPE_SYSTEM, ORG_TYPE_PROV)
  );
}

export function isPracticeOrganization(org: Organization): boolean {
  return organizationHasTypeCoding(org, ORG_TYPE_SYSTEM, ORG_TYPE_PROV);
}

export function organizationIdFromReference(
  ref: string | undefined,
): string | undefined {
  if (!ref?.startsWith('Organization/')) return undefined;
  const id = ref.slice('Organization/'.length).trim();
  return id || undefined;
}

export function classifyStaffRoleSet(roles: readonly string[]): StaffRoleOrgKind {
  let platform = false;
  let practice = false;
  for (const role of roles) {
    if (!role) continue;
    if (PLATFORM_ROLE_SET.has(role)) platform = true;
    else if (PRACTICE_ROLE_SET.has(role)) practice = true;
  }
  if (platform && practice) return 'mixed';
  if (platform) return 'platform';
  if (practice) return 'practice';
  return 'none';
}

/**
 * Prospective staff roles for invite / role-change / reactivate.
 * `additionalRoles: []` clears extras; omitted additionalRoles keeps current extras on role-change.
 */
export function mergeStaffRoles(input: {
  operation: string;
  role?: string;
  additionalRoles?: string[];
  currentRoles: readonly string[];
}): string[] {
  const primary = input.role?.trim();
  if (!primary) return [...input.currentRoles];
  if (input.additionalRoles !== undefined) {
    return [primary, ...input.additionalRoles.filter((r) => r && r !== primary)];
  }
  if (input.operation === 'role-change' || input.operation === 'reactivate') {
    return [primary, ...input.currentRoles.filter((r) => r !== primary)];
  }
  return [primary];
}
